import ApiDatabase from 'server';
import { companiesPage, companiesQuery } from './crmCompanies';
import crmRecords from './crmRecords';
import { forget } from './crmRequests';

/**
 * The api of the CRM pages: the home page, the tasks, a task's panel and form,
 * its comments and its activity. Each call has the `(payload, resolve,
 * reject)` shape of server/index.js.
 *
 * Every call goes to the real api; the record pages' reads compose several of
 * them (crmRecords.js). The CRM demo (src/test/crmDemo/) only serves the tests
 * of the screens: no call of the app reaches it.
 *
 *   getTasks            POST admin/crm/tasks               { people: [ids] | 'team' }    api
 *   countOpenTasks      POST admin/crm/tasks/count         { }                           api
 *   getTask             POST admin/crm/task                { id }                        api, by id
 *   createTask          POST admin/crm/task/create         { fields }                    api
 *   updateTask          POST admin/crm/task/update         { id, fields }                api, by id
 *   deleteTask          POST admin/crm/task/delete         { id }                        api, by id
 *   restoreTask         POST admin/crm/task/restore        { id }                        api, by id
 *   listDeletedTasks    GET  admin/crm/tasks/deleted       { }                           api
 *                       → the tasks of the list deleted lately, each with deletedAt and
 *                       deletedBy { _id, name }, the latest first (50 at most)
 *   setTaskDone         POST admin/crm/task/done           { id, done }                  api, by id
 *   assignTask          POST admin/crm/task/assign         { id, assigneeId }            api, by id
 *   moveTask            POST admin/crm/task/due            { id, dueOn }                 api, by id
 *   getTaskActivity     POST admin/crm/task/activity       { taskId }                    api, by id
 *   getMentions         POST admin/crm/mentions            { page, pageSize, filter }    api
 *   getTeam             POST admin/crm/team                { }                           api
 *   getContacts         POST admin/crm/contacts            { }                           api
 *   getTaskTempWorkers  POST admin/crm/tempworkers         { search }                    api
 *   getHome             POST admin/crm/home                { }                           api
 *   setAppointmentDone  POST admin/crm/appointment/status  { id, done }                  api, by id
 *   setCallbackDone     POST admin/crm/callback/done       { id, done }                  api
 *   listComments        POST admin/crm/task/comments       { taskId }                    api, by id
 *   addComment          POST admin/crm/task/comment        { taskId, body, mentions }    api, by id
 *   listAppointmentComments  POST admin/crm/appointment/comments  { id }                 api, by id
 *   addAppointmentComment    POST admin/crm/appointment/comment   { id, body, mentions } api, by id
 *   updateComment      POST admin/crm/task/comment/update   { taskId, commentId, body, mentions }  api, by id (api: no taskId)
 *   deleteComment      POST admin/crm/task/comment/delete   { taskId, commentId }                 api, by id (api: no taskId)
 *   updateAppointmentComment  POST admin/crm/appointment/comment/update  { id, commentId, body, mentions }  api, by id
 *   deleteAppointmentComment  POST admin/crm/appointment/comment/delete  { id, commentId }                 api, by id
 *   respondAppointment  POST admin/crm/appointment/respond { id, response }              api, by id
 *   markTaskRead        POST admin/crm/task/read           { id }                        api, by id
 *   listCompanies       POST admin/crm/companies           { tab, search, filters, page }  api
 *   assignCompany       POST admin/crm/company/assign      { companyId, role: 'commercial' | 'recruiter', personId }  api
 *   getCompanyCrm       POST admin/crm/company/crm         { companyId }                 api
 *   completeClient      POST admin/crm/company/client      { companyId, company }        api (JBWEB-1422)
 *                       a prospect becomes a client: `company` is the body of company/add,
 *                       written on the prospect's own company (no second company)
 *   moveCompanyToOld    POST admin/crm/company/old         { companyId, recruiterId }    api (JBWEB-1423)
 *                       a new client becomes an old one, followed by its recruiter (520,
 *                       else 500) with its commercial
 *   moveCompanyToNew    POST admin/crm/company/new         { companyId }                 api (JBWEB-1423)
 *                       an old client back to the new ones: oldClientSince unset, its
 *                       recruiter kept; 409 NOT_AN_OLD_CLIENT
 *   markCompanyLost     POST admin/crm/company/lost        { companyId, lostReason, lostDetails?, relaunchOn | null, commercialId? }  api (JBWEB-1423)
 *                       lostReason: price | competitor | noNeed | direct | unreachable | closed;
 *                       a relaunch goes to the company's commercial, so commercialId is
 *                       asked for when it has none (400 COMMERCIAL_REQUIRED)
 *   reactivateProspect  POST admin/crm/prospect/reactivate { companyId }                 api
 *                       a lost company back to prospect, its pending relaunch canceled
 *   The moves of a stage are the agency manager's and the company's commercial's
 *   (helper/crmStageRights.js; 403 STAGE_FORBIDDEN, PROSPECT_FORBIDDEN).
 *   saveCompanyContact  POST admin/crm/company/contact/save   { companyId, contact }     api (JBWEB-1424)
 *   deleteCompanyContact POST admin/crm/company/contact/delete { companyId, contactId }  api (JBWEB-1424)
 *   linkCompanyContact  POST admin/crm/company/contact/link   { companyId, contactId }   api (JBWEB-1424)
 *   deactivateCompanyMember POST admin/crm/company/member/deactivate { companyId, userId }  api (JBWEB-1428)
 *   reactivateCompanyMember POST admin/crm/company/member/reactivate { companyId, userId }  api (JBWEB-1428)
 *                       → { companyId, userId, deactivatedAt | null }; the entry stays, its role and
 *                       perimeters kept. 400 INVALID_ID; 404 COMPANY_NOT_FOUND, MEMBER_NOT_FOUND;
 *                       409 LAST_ADMIN (the company's last administrator not deactivated)
 *                       a contact of the company without an account: { id, firstname,
 *                       lastname, phone, email, position, userId }; getCompanyCrm lists them
 *                       as `contacts`. save answers { contact } (no id adds it), delete
 *                       { contactId }, link { contact } once its invitation made an account
 *                       of its e-mail (`userId`). 400 INVALID_ID, INVALID_CONTACT (an empty
 *                       position is one); 403 COMPANY_CONTACT_FORBIDDEN (500 and 510 only);
 *                       404 COMPANY_NOT_FOUND, COMPANY_CONTACT_NOT_FOUND; 409
 *                       COMPANY_CONTACTS_FULL, CONTACT_NOT_INVITED
 *   setCompanyEmail     POST admin/crm/company/email       { companyId, email | null }   api (JBWEB-1425)
 *                       the company's general address, answered { companyId, email } and read
 *                       back as `crm.email` of getCompanyCrm; 400 INVALID_ID, INVALID_EMAIL;
 *                       403 COMPANY_CONTACT_FORBIDDEN (500 and 510 only); 404 COMPANY_NOT_FOUND
 *   setContactPosition  POST admin/crm/contact/position    { email, position }           api (JBWEB-1425)
 *                       a person's position, wherever their e-mail is (their account's
 *                       `user.position`, the contacts without an account); getCompanyCrm
 *                       answers those of the members as `memberPositions`. 400 INVALID_EMAIL,
 *                       INVALID_CONTACT; 403 COMPANY_CONTACT_FORBIDDEN
 *   getCompanyActivity  POST admin/crm/company/activity    { companyId }                 api (read by getCompanyRecord)
 *                       { tasks, appointments, calls, notes } as getGroupActivity, plus clientEvents
 *                       [{ id, kind, at, actor, companyId, details }], the latest first
 *   getCompanyResults   POST admin/crm/company/results     { companyId }                 api (read by getCompanyRecord)
 *                       { since, appointmentsDone, appointmentsNotDone, missionsRequested,
 *                       invoicedRevenue, invoiceTotals: { revenue, revenuePrev, unpaidAmount,
 *                       unpaidCount } } over the last 365 days, for every CRM role
 *                       (500, 510, 515, 520); the page's tiles read invoiceTotals
 *   setCompanySize      POST admin/crm/company/size        { companyId, size | null }    api
 *                       → { size, sizeSource, etp }; 400 INVALID_SIZE
 *   setCompanyPhone     POST admin/crm/company/phone       { companyId, phone | null }   api
 *                       → { phone }; 403 COMPANY_CONTACT_FORBIDDEN (500 and 510 only)
 *   deleteCompany       POST admin/crm/company/delete      { companyId, reason, details } api (500 only)
 *                       archives the company, whatever its stage, missions included: it leaves the
 *                       lists of the CRM and of prospecting; 400 INVALID_DELETE_REASON
 *   getArchivedCompanies POST admin/crm/company/archived   { }                           api (500 only)
 *                       → [{ companyId, name, stage, archivedAt, archivedBy: { id, name } | null,
 *                       reason, details }], the latest archived first (the api's { data } unwrapped)
 *   restoreCompany      POST admin/crm/company/restore     { companyId }                 api (500 only), by id
 *                       → { companyId, stage } (unwrapped); 400 INVALID_ID; 404 COMPANY_NOT_FOUND;
 *                       409 COMPANY_NOT_ARCHIVED
 *   logRecordCall       POST admin/crm/call                { companyId | groupId, contactName, result, note }  api
 *                       → the call, as one of getGroupActivity's; 400 INVALID_CALL
 *   getClientSizeSettings   POST admin/crm/settings/client-sizes         { }              api (500 only)
 *   saveClientSizeSettings  POST admin/crm/settings/client-sizes/update  { grand_compte?, pme?, visits? }  api (500 only)
 *                       → { grand_compte, pme, visits: { bigPerMonth 1-8, midEveryWeeks 1-26,
 *                       smallEveryMonths 1-12, placeWithinDays 1-60 } }; a key left out is kept.
 *                       400 INVALID_CLIENT_SIZES
 *   getCallSettings     POST admin/crm/settings/calls         { }                         api (500 only)
 *   saveCallSettings    POST admin/crm/settings/calls/update  { goals?: [{ personId, discovery, followup }], rules? }  api (500 only)
 *                       → { goals: [{ personId, firstname, lastname, discovery, followup }], rules:
 *                       { noAnswerThreshold 1-10, callWindow: { start, end } ('HH:MM', start < end) } },
 *                       one goal per active 500 and 510; whole calls a day from 0 to 100; only the
 *                       entries sent are written, a key left out is kept. 400 INVALID_GOAL,
 *                       UNKNOWN_PERSON (not an active 500 or 510), INVALID_CALL_RULES
 *   A company deleted answers 404 COMPANY_NOT_FOUND everywhere.
 *   getGroupRecord     POST admin/crm/group               { groupId }                   api (crmRecords.js)
 *   getContactRecord   POST admin/crm/contact/record      { contactId } | { email }     api (crmRecords.js)
 *                       → { person, companies, activity }; the page gives { contactId }.
 *                       400 INVALID_ID (a former `hs-<id>` address too), 404 CONTACT_NOT_FOUND
 *   saveContact         POST admin/crm/contact/save        { contactId, firstname, lastname, phone, email }  api (JBWEB-1431)
 *                       a person without an account, in every company and group of their e-mail
 *                       → { person }; 400 INVALID_ID, INVALID_CONTACT; 403 COMPANY_CONTACT_FORBIDDEN
 *                       (500 and 510 only); 404 CONTACT_NOT_FOUND; 409 CONTACT_HAS_ACCOUNT, CONTACT_EMAIL_TAKEN
 *   getGroupActivity    POST admin/crm/group/activity      { groupId }                   api (read by getGroupRecord)
 *   saveGroupInfo       POST admin/crm/group/info/save     { groupId, phone, email }     api
 *                       → { group: { id, phone, email } }; null unsets a field; 400 INVALID_ID,
 *                       INVALID_GROUP_INFO, INVALID_PHONE, INVALID_EMAIL; 403 COMPANY_CONTACT_FORBIDDEN
 *                       (500 and 510 only); 404 GROUP_NOT_FOUND
 *   deleteGroup         POST admin/crm/group/delete        { groupId }                   api (deleteCrmGroup)
 *                       → { group: { id, deletedAt } }; 500 only (403 FORBIDDEN_ROLE); 400 INVALID_ID;
 *                       404 GROUP_NOT_FOUND (absent or already deleted). Its companies stay, out of it.
 *   saveGroupContact    POST admin/crm/group/contact/save  { groupId, contact }          api
 *   deleteGroupContact  POST admin/crm/group/contact/delete { groupId, contactId }       api
 *   removeCompanyFromGroup  POST admin/crm/group/company/remove  { groupId, companyId }  api
 *   addRecordNote       POST admin/crm/note/create         { body, links, callId?, phoneCallId? }  api
 *   updateRecordNote    POST admin/crm/note/update         { id, body, links }           api
 *   deleteRecordNote    POST admin/crm/note/delete         { id }                        api
 *   listRecordNotes     POST admin/crm/notes               { companyId | groupId | contactId }  api
 *   sendRecordEmail     POST admin/crm/record/email/send   { companyId | groupId, contactName, to, cc, bcc, subject, body,
 *                       senderEmail?, scheduledAt? }  api
 *   listRecordEmails    POST admin/crm/record/emails       { companyId } | { groupId }   api (read by the record pages)
 *   cancelRecordEmail   POST admin/crm/record/email/cancel { id }                        api
 *
 * sendRecordEmail answers { email }; listRecordEmails { data: [email],
 * intercom: 'ok' | 'unavailable' } (the received ones come from Intercom; when
 * it does not answer, the sent ones still come). An e-mail is { id, direction:
 * 'sent' | 'received', at, authorId, contactName, address, to, cc, subject,
 * body, source: 'jobbiz' | 'intercom', conversationId?, senderEmail }; a sent one also
 * carries status 'scheduled' | 'sending' | 'sent' | 'failed' (absent = 'sent')
 * and scheduledAt: a scheduled or failed one has `at` = scheduledAt, a
 * canceled one is not listed. `senderEmail` is the address it left (or
 * leaves) from, null for the e-mails sent before it was chosen. Sent, it is
 * rh@jobbiz.fr or the signed-in account's own address when that ends with
 * @jobbiz.fr (absent: rh@jobbiz.fr); it becomes the from and the replyTo,
 * the sender's name unchanged. Refusals: 400 INVALID_EMAIL_FIELD,
 * INVALID_SENDER_EMAIL (any other sender address),
 * INVALID_SCHEDULED_AT (scheduledAt not between now + 1 min and now + 30
 * days), and EMAIL_SENDING_DISABLED, EMAIL_SEND_FAILED of the sending service.
 * cancelRecordEmail (its author only) answers { email: { id, status:
 * 'canceled' } }; 403 EMAIL_CANCEL_FORBIDDEN, 404 EMAIL_NOT_FOUND, 409
 * ALREADY_SENT (sending or sent), 409 EMAIL_NOT_SCHEDULED (failed).
 *
 *   getRecordCalls      POST admin/crm/record/calls        { companyId | groupId }       api
 *                       the Wildix calls of the record's numbers → { data: [{ id, at, direction,
 *                       missed, talkSeconds, ringSeconds, userId, remotePhone, contactName }] }
 *
 * A group's contact is { id, firstname, lastname, email, phone, position };
 * with `contact.id`, saveGroupContact edits it, and it answers { contact }.
 * deleteGroupContact answers { id }. Only a Responsable d'agence or a
 * Commercial writes them (403 GROUP_CONTACT_FORBIDDEN); 404 GROUP_NOT_FOUND,
 * GROUP_CONTACT_NOT_FOUND; 400 INVALID_CONTACT (a first name or a last name,
 * a valid e-mail); 409 GROUP_CONTACTS_FULL past 50. removeCompanyFromGroup,
 * open to every CRM role, answers { groupId, companyId }: 404
 * GROUP_NOT_FOUND, 409 COMPANY_NOT_IN_GROUP.
 *
 * getGroupActivity answers { tasks, appointments, calls, notes } of the
 * group and of its companies, the latest first, 200 at most of each: tasks
 * as getTasks lists them; appointments { id, title, category, status,
 * startsAt, endsAt, allDay, ownerId, owner, companyId, groupId } (neither
 * canceled nor someone else's private ones); calls { id, at, result, note,
 * contactName, companyId, groupId, author }; notes as below. 404
 * GROUP_NOT_FOUND.
 *
 * A note's `links` are those of a task, without temp workers (1 to 20,
 * [{ type: 'company' | 'group' | 'contact', id, companyId? }]); its body is 1
 * to 5,000 characters. addRecordNote answers { note } (with `callId`, the
 * call's company or group is linked if missing), updateRecordNote { note },
 * deleteRecordNote { id }, listRecordNotes { notes } (the notes linked to that
 * very record, the latest first, 200 at most; contactId: the account's id or
 * 'name:<name>'). A note is { id, body, links: [{ type, id, label, sub,
 * companyId }], callId, authorId, author, createdAt, editedAt, canEdit (the
 * author), canDelete (the author, or a Responsable d'agence) }. Refusals: 400
 * NOTE_BODY_REQUIRED, NOTE_TOO_LONG, INVALID_LINKS, INVALID_CALL; 403
 * NOTE_EDIT_FORBIDDEN, NOTE_DELETE_FORBIDDEN; 404 NOTE_NOT_FOUND.
 *
 * The `doneAgenda` of getTasks is real: the appointments done and the
 * callbacks done of the people of the page, the latest first, each
 * { kind: 'appointment' | 'callback', id, title, doneAt, dueOn,
 * company: { id, name } | null, contactName, access: { canEdit } }
 * (a callback's title is null: the screen draws it from tasks.callbackTitle).
 *
 * A task's `links` (getTasks, getHome, getTask, every detailed answer) are
 * [{ type: 'company' | 'group' | 'contact' | 'tempWorker', id, label, sub?,
 * companyId?, validated? }]; a contact's also carries companyRole (its role
 * at its company, or null) and phone (its own, never the company's, or '');
 * a record gone since keeps its link with label ''.
 * createTask and updateTask send `fields.links` as [{ type, id, companyId? }]
 * (20 at most): a contact with a record is { type: 'contact', id: userId,
 * companyId }, the userId of its getContacts option; a contact without one is
 * { type: 'contact', id: 'name:<name>', companyId? }. The links are the only
 * tie of a task to its records: the api draws company, group and contactName
 * from them; a link that does not exist or does not hold answers 400
 * INVALID_LINKS, a call without a company, a group or a contact of a company
 * 400 TASK_TARGET_REQUIRED.
 *
 * getTaskTempWorkers answers [{ id, firstname, lastname, city, validated }],
 * 20 at most, whose first name or last name starts with `search` (2 to 50
 * characters, 400 INVALID_SEARCH otherwise).
 *
 * setCallbackDone ticks or unticks a callback of doneAgenda and answers
 * { id, done }: 404 CALLBACK_NOT_FOUND, 403 CALLBACK_FORBIDDEN (not the
 * caller's), 409 CALLBACK_PENDING_EXISTS (reopening while another callback of
 * the company waits).
 *
 * A comment view (list, add, update) is { id, authorId, author, body, mentions,
 * createdAt, editedAt (ISO string or null), canEdit (the viewer is the author),
 * canDelete (the author, or a Responsable d'agence) }. Deleting answers { id }.
 * A missing comment rejects with errorCode COMMENT_NOT_FOUND.
 *
 * getHome answers the shape of `homeView` (src/test/crmDemo/crmViews.js). Its
 * `appointments` come from the api: the caller's of the day (Martinique), not
 * canceled, by startsAt, in the shape of the planning's. `appointmentsToPlace` counts
 * the caller's visits to place (zeros as long as the api does not count them).
 * `calls` is filled for a role that prospects (null otherwise); its
 * `toQualify` counts the Wildix calls to qualify, `firstToQualify` being the
 * oldest ({ phoneCallId, companyId }) or null.
 *
 * "By id": a call on a task, a comment or an appointment goes to the api for
 * an ObjectId only; any other id rejects with errorCode INVALID_ID, without a
 * call, as the api would.
 *
 * listCompanies sends `tab` (prospects | new | old | lost), `search` (100
 * characters at most, no fuzzy match) and `filters` (companiesQuery,
 * crmCompanies.js: departments, filterCategory, supplierSearch,
 * missionCountMode, missionCountMin, incompleteRemuneration, sizes
 * ['grand_compte' | 'pme' | 'tpe'], then action 'all' | 'with' | 'without'
 * and commercial 'all' | 'none' | id, and on the prospects tab unassigned,
 * true for the inbound prospects without a commercial); a value the
 * api does not know answers 400 INVALID_FILTERS. The api answers { data,
 * page, totalPages, counts: { prospects, new, old, lost }, sizeThresholds:
 * { grand_compte, pme } } (the minimum ETP of each class, from the admin
 * console), 20 rows a page, which the page reads as { companies, counts,
 * count, page, pages, perPage, sizeThresholds } (companiesPage). A row holds the company (name_commercial, reason_social,
 * siret, department, pennylane_category_id, createdAt, missionCount,
 * bigAccounts, group { id, name } | null, size 'grand_compte' | 'pme' | 'tpe' |
 * null, sizeSource 'etp' | 'manual' | null), its CRM state (stage,
 * clientSince, lostAt, lostReason, relaunchOn, commercialId, recruiterId)
 * and the
 * signed-in user's own next action: { kind: 'task', id, title, dueOn,
 * dueTime, at } | { kind: 'appointment', id, category, title, at } |
 * { kind: 'callback', id, at } | null.
 *
 * assignCompany answers { companyId, commercialId, recruiterId }: 400
 * INVALID_ASSIGN_ROLE, 400 INVALID_ASSIGNEE (not an active 510 for a
 * commercial, a 520 or a 500 for a recruiter), 409 ASSIGN_NOT_CLIENT (a
 * recruiter for a company that is not a client), 403 ASSIGN_FORBIDDEN, 404
 * COMPANY_NOT_FOUND.
 *
 * The record pages (a company, a group, a contact) read crmRecords.js; the
 * planning's contract is listed in src/test/crmDemo/crmPlanningApi.js; the
 * statistics and the goals go to the api (contract beside their entries in allCrmApi).
 *
 * Only the keys of the contract are sent: the api knows who asks.
 */


// The goals the agency manager sets; `calls` is drawn by the api from the
// daily call goals (Taille des clients), never sent.
const SAVED_GOALS = ['rdvTaken', 'rdvDone', 'sales', 'revenue'];
const REVIEW_SCALES = ['confidence', 'questioning', 'objections', 'energy'];

const isRealId = (id) => typeof id === 'string' && /^[0-9a-f]{24}$/i.test(id);

// The keys of the contract, out of a page's payload.
const pick =
  (...keys) =>
  (payload) =>
    Object.fromEntries(keys.map((key) => [key, payload[key]]));

// For the settings, where a key left out is kept by the api: only the keys
// given are sent.
const pickGiven =
  (...keys) =>
  (payload) =>
    Object.fromEntries(keys.filter((key) => payload[key] !== undefined).map((key) => [key, payload[key]]));

// Read at call time, so that a test's mock of server/index.js is the one called.
const toApi = (method, bodyOf) => (payload, resolve, reject) =>
  ApiDatabase[method](bodyOf(payload ?? {}), resolve, reject);

// Rejected as the api rejects an id that is not an ObjectId, so that the
// screens translate it as any refusal.
const invalidId = () =>
  Object.assign(new Error('Invalid id'), {
    response: { status: 400, data: { error: 'Invalid id', errorCode: 'INVALID_ID' } },
  });

const byId =
  (method, bodyOf, idOf = (payload) => payload.id) =>
  (payload, resolve, reject) =>
    isRealId(idOf(payload ?? {}))
      ? toApi(method, bodyOf)(payload, resolve, reject)
      : reject(invalidId());

// The undo of a deletion hands back the deleted task, the api wants its id.
const restoredId = (payload) => payload.id ?? payload.task?.id;

const allCrmApi = {
  // getCompanyRecord, getGroupRecord, getGroupInvoiceTotals, getContactRecord,
  // listGroupCandidates, makeCompanyGroup, addCompanyToGroup.
  ...crmRecords,
  getTasks: toApi('getCrmTasks', pick('people')),
  countOpenTasks: toApi('countCrmOpenTasks', pick()),
  createTask: toApi('createCrmTask', pick('fields')),
  getHome: toApi('getCrmHome', pick()),
  // One page of the comments that name the signed-in user, the latest first:
  // { data: [{ id, body, createdAt, author: { id, firstname, lastname }, task: { id, title }, unread }],
  //   page, pageSize, total, totalPages, counts: { all, unread } }
  // `unread`: the comment is newer than the last time its reader opened the task.
  getMentions: toApi('getCrmMentions', pick('page', 'pageSize', 'filter')),
  getTeam: toApi('getCrmTeam', pick()),
  listCompanies: (payload, resolve, reject) =>
    ApiDatabase.getCrmCompanies(
      companiesQuery(payload ?? {}),
      (answer) => resolve(companiesPage(answer, payload?.tab ?? 'prospects')),
      reject,
    ),
  assignCompany: toApi('assignCrmCompany', pick('companyId', 'role', 'personId')),
  // { crm: { stage, clientSince, oldClientSince, lostAt, lostReason, lostDetails, relaunchOn, commercialId, recruiterId, email,
  //     phone, size: 'grand_compte' | 'pme' | 'tpe' | null, sizeSource: 'etp' | 'manual' | null, etp },
  //   nextAction: { kind: 'task', id, title, dueOn, dueTime, at } | { kind: 'appointment', id, category, title, at }
  //     | { kind: 'callback', id, at } | null,
  //   contacts: [{ id, firstname, lastname, phone, email, position, userId }],
  //   memberPositions: { [userId]: position } }; 400 INVALID_ID, 404 COMPANY_NOT_FOUND
  getCompanyCrm: toApi('getCrmCompanyCrm', pick('companyId')),
  // { personId, status: '' | 'ambassadeur' } -> { personId, leadStatus }
  setLeadStatus: toApi('setCrmPersonLead', pick('personId', 'status')),
  getLeadStatus: toApi('getCrmPersonLead', pick('personId')),
  saveCompanyContact: toApi('saveCrmCompanyContact', pick('companyId', 'contact')),
  deleteCompanyContact: toApi('deleteCrmCompanyContact', pick('companyId', 'contactId')),
  // A member taken off a company keeps its entry, deactivated, and comes back
  // with its role and perimeters: { companyId, userId } -> { companyId, userId, deactivatedAt }.
  deactivateCompanyMember: toApi('deactivateCrmCompanyMember', pick('companyId', 'userId')),
  reactivateCompanyMember: toApi('reactivateCrmCompanyMember', pick('companyId', 'userId')),
  linkCompanyContact: toApi('linkCrmCompanyContact', pick('companyId', 'contactId')),
  // '' (the field emptied) unsets it, sent as null.
  setCompanyEmail: toApi('setCrmCompanyEmail', ({ companyId, email }) => ({ companyId, email: email || null })),
  setContactPosition: toApi('setCrmContactPosition', pick('email', 'position')),
  saveContact: toApi('saveCrmContact', pick('contactId', 'firstname', 'lastname', 'phone', 'email')),
  // null unsets the size chosen by hand.
  setCompanySize: toApi('setCrmCompanySize', ({ companyId, size }) => ({ companyId, size: size ?? null })),
  // '' (the field emptied) unsets the switchboard, sent as null.
  setCompanyPhone: toApi('setCrmCompanyPhone', ({ companyId, phone }) => ({ companyId, phone: phone || null })),
  deleteCompany: toApi('deleteCrmCompany', ({ companyId, reason, details }) => ({ companyId, reason, details: details ?? '' })),
  // These two answer { data }, unlike the other company routes.
  getArchivedCompanies: (payload, resolve, reject) =>
    ApiDatabase.getCrmArchivedCompanies({}, (answer) => resolve(answer?.data ?? []), reject),
  restoreCompany: (payload, resolve, reject) =>
    byId('restoreCrmCompany', pick('companyId'), (one) => one.companyId)(
      payload,
      (answer) => resolve(answer?.data ?? null),
      reject,
    ),
  // Exactly one record: the group when there is one, else the company.
  logRecordCall: toApi('createCrmCall', ({ companyId, groupId, contactName, result, note }) => ({
    ...(groupId ? { groupId } : { companyId }),
    contactName: contactName ?? '',
    result,
    note: note ?? '',
  })),
  getClientSizeSettings: toApi('getCrmClientSizeSettings', pick()),
  saveClientSizeSettings: toApi('saveCrmClientSizeSettings', pickGiven('grand_compte', 'pme', 'visits')),
  getCallSettings: toApi('getCrmCallSettings', pick()),
  saveCallSettings: toApi('saveCrmCallSettings', pickGiven('goals', 'rules')),
  completeClient: toApi('completeCrmClient', pick('companyId', 'company')),
  moveCompanyToOld: toApi('moveCrmCompanyToOld', pick('companyId', 'recruiterId')),
  moveCompanyToNew: toApi('moveCrmCompanyToNew', pick('companyId')),
  // lostDetails and commercialId only when given: the api keeps them optional.
  markCompanyLost: toApi('markCrmCompanyLost', ({ companyId, lostReason, lostDetails, relaunchOn, commercialId }) => ({
    companyId,
    lostReason,
    ...(lostDetails ? { lostDetails } : {}),
    relaunchOn: relaunchOn ?? null,
    ...(commercialId ? { commercialId } : {}),
  })),
  reactivateProspect: toApi('reactivateCrmProspect', pick('companyId')),
  getContacts: toApi('getCrmContacts', pick()),
  getTaskTempWorkers: toApi('searchCrmTaskTempWorkers', pick('search')),
  getTask: byId('getCrmTask', pick('id')),
  updateTask: byId('updateCrmTask', pick('id', 'fields')),
  deleteTask: byId('deleteCrmTask', pick('id')),
  // The api answers { tasks }: the list alone is handed back.
  listDeletedTasks: (payload, resolve, reject) =>
    ApiDatabase.listCrmDeletedTasks({}, (answer) => resolve(answer?.tasks ?? []), reject),
  restoreTask: byId(
    'restoreCrmTask',
    (payload) => ({ id: restoredId(payload) }),
    restoredId,
  ),
  setTaskDone: byId('setCrmTaskDone', pick('id', 'done')),
  assignTask: byId('assignCrmTask', pick('id', 'assigneeId')),
  moveTask: byId('moveCrmTask', pick('id', 'dueOn')),
  getTaskActivity: byId(
    'getCrmTaskActivity',
    pick('taskId'),
    (payload) => payload.taskId,
  ),
  listComments: byId(
    'getCrmTaskComments',
    pick('taskId'),
    (payload) => payload.taskId,
  ),
  addComment: byId(
    'addCrmTaskComment',
    pick('taskId', 'body', 'mentions'),
    (payload) => payload.taskId,
  ),
  updateComment: byId(
    'updateCrmTaskComment',
    pick('commentId', 'body', 'mentions'),
    (payload) => payload.taskId,
  ),
  deleteComment: byId(
    'deleteCrmTaskComment',
    pick('commentId'),
    (payload) => payload.taskId,
  ),
  markTaskRead: byId('markCrmTaskRead', pick('id')),
  getPlanning: toApi('getCrmPlanning', pick('from', 'to', 'ownerId')),
  // No ownerId: the api gives the appointment to the owner of the visit.
  placeVisit: toApi('placeCrmVisit', pick('visitId', 'startsAt', 'travelMinutes')),
  // A tour: all its stops placed, or none (409 VISIT_ALREADY_PLACED, 400 PAST_START…).
  placeVisits: toApi('placeCrmVisits', pick('stops')),
  // groupId (a group's record), beside companyId: an existing group, else
  // 400 UNKNOWN_GROUP; never sent to Google.
  createAppointment: toApi(
    'createCrmAppointment',
    pick(
      'ownerId',
      'category',
      'title',
      'description',
      'allDay',
      'startDay',
      'endDay',
      'startsAt',
      'endsAt',
      'companyId',
      'groupId',
      'location',
      'contactName',
      'attendees',
      'meet',
      'reminders',
      'colorId',
      'transparency',
      'visibility',
      'recurrence',
      'toPlace',
    ),
  ),
  getAppointment: byId('getCrmAppointment', pick('id')),
  updateAppointment: byId(
    'updateCrmAppointment',
    pick('id', 'fields', 'scope'),
  ),
  setAppointmentDone: byId(
    'setCrmAppointmentDone',
    pick('id', 'done'),
  ),
  // A note on a call: `callId` a call logged in the CRM, `phoneCallId` a Wildix call.
  addRecordNote: toApi('createCrmNote', ({ body, links, callId, phoneCallId }) => ({
    body,
    links,
    ...(callId ? { callId } : {}),
    ...(phoneCallId ? { phoneCallId } : {}),
  })),
  updateRecordNote: toApi('updateCrmNote', pick('id', 'body', 'links')),
  deleteRecordNote: toApi('deleteCrmNote', pick('id')),
  // Exactly one record: the first of companyId, groupId, contactId given.
  listRecordNotes: toApi('getCrmNotes', (payload) => {
    const key = ['companyId', 'groupId', 'contactId'].find((one) => payload[one]);
    return key ? { [key]: payload[key] } : {};
  }),
  getGroupActivity: toApi('getCrmGroupActivity', pick('groupId')),
  getCompanyActivity: toApi('getCrmCompanyActivity', pick('companyId')),
  getCompanyResults: toApi('getCrmCompanyResults', pick('companyId')),
  // Exactly one record: the group when there is one, else the company.
  // `scheduledAt` (ISO): sent later, by the api's dispatch. Without
  // `senderEmail`, the api sends from rh@jobbiz.fr.
  sendRecordEmail: toApi('sendCrmRecordEmail', ({ companyId, groupId, contactName, to, cc, bcc, subject, body, senderEmail, scheduledAt }) => ({
    ...(groupId ? { groupId } : { companyId }),
    contactName,
    to,
    cc,
    bcc,
    subject,
    body,
    ...(senderEmail ? { senderEmail } : {}),
    ...(scheduledAt ? { scheduledAt } : {}),
  })),
  listRecordEmails: toApi('getCrmRecordEmails', ({ companyId, groupId }) => (groupId ? { groupId } : { companyId })),
  cancelRecordEmail: toApi('cancelCrmRecordEmail', pick('id')),
  // The Wildix calls of one record, matched by its phone numbers: the first of companyId, groupId given.
  getRecordCalls: toApi('getCrmRecordCalls', (payload) => {
    const key = ['companyId', 'groupId'].find((one) => payload[one]);
    return key ? { [key]: payload[key] } : {};
  }),
  // The group's own phone and e-mail, both sent: '' (the field emptied) unsets it, sent as null.
  saveGroupInfo: toApi('saveCrmGroupInfo', ({ groupId, phone, email }) => ({ groupId, phone: phone || null, email: email || null })),
  // A deleted group leaves the groups kept a moment (crmRequests.js), as a company removed does.
  deleteGroup: (payload, resolve, reject) =>
    toApi('deleteCrmGroup', pick('groupId'))(
      payload,
      (data) => {
        forget('groups');
        resolve(data);
      },
      reject,
    ),
  saveGroupContact: toApi('saveCrmGroupContact', pick('groupId', 'contact')),
  deleteGroupContact: toApi('deleteCrmGroupContact', pick('groupId', 'contactId')),
  // The groups kept a moment (crmRequests.js) are read again after it.
  removeCompanyFromGroup: (payload, resolve, reject) =>
    toApi('removeCrmGroupCompany', pick('groupId', 'companyId'))(
      payload,
      (data) => {
        forget('groups');
        resolve(data);
      },
      reject,
    ),
  // The callbacks of the list come from the api only (doneAgenda).
  setCallbackDone: toApi('setCrmCallbackDone', pick('id', 'done')),
  removeAppointment: byId(
    'deleteCrmAppointment',
    pick('id', 'scope'),
  ),
  listAppointmentComments: byId(
    'listCrmAppointmentComments',
    pick('id'),
  ),
  addAppointmentComment: byId(
    'addCrmAppointmentComment',
    pick('id', 'body', 'mentions'),
  ),
  updateAppointmentComment: byId(
    'updateCrmAppointmentComment',
    pick('commentId', 'body', 'mentions'),
  ),
  deleteAppointmentComment: byId(
    'deleteCrmAppointmentComment',
    pick('commentId'),
  ),
  // An invitee's answer: response is 'accepted' | 'declined' | 'tentative'.
  respondAppointment: byId(
    'respondCrmAppointment',
    pick('id', 'response'),
  ),

  /*
   * The statistics page (pages/stats) and the goals consoles (AdminGoals,
   * AdminAgencyGoals). `people`: ids of 500 or 510 accounts, absent or [] =
   * the signed-in user; a 510 naming another gets 403 TEAM_VIEW_FORBIDDEN, an
   * unknown id 400 INVALID_PEOPLE. Errors: statsErrors and agencyGoalsErrors
   * of the locales.
   *
   *   getStatsDays     stats/days    { from, to, people } -> a row per day from
   *     `from` to `to` (excluded, « 2026-10-23 », Martinique days, 800 at most,
   *     else 400 INVALID_RANGE), the figures of `people` added up, 0 for a day
   *     to come: { day, calls, pickedUp (all but no_answer), discussions
   *     (answered), rdvTaken (on the day booked), rdvDone, rdvNoShow (on their
   *     day), inbound, sales (turned client, frozen at the sale), revenue
   *     (TTC invoices of the sales of the last 365 days, before the first one
   *     an estimate from the timesheets), revenueEstimated (that estimate) }
   *   getStatsReviews  stats/reviews { people } -> oldest first [{ id,
   *     personId, weekStart, confidence, questioning, objections, energy
   *     (1 to 5), victory, challenge, createdAt }]
   *   saveStatsReview  stats/review  { weekStart, confidence, questioning,
   *     objections, energy, victory, challenge } -> the review, the signed-in
   *     user's only; replaces the week's; a challenge makes the task « Défi de
   *     la semaine : … » of the next Monday at 9:00. 400 INVALID_WEEK (not a
   *     Monday, or after this week), INVALID_REVIEW
   *   suggestStatsChallenge  stats/review/suggest { weekStart, victory? } ->
   *     { challenge } drawn by the AI; 503 AI_NOT_CONFIGURED, AI_UNAVAILABLE
   *   getStatsGoals    stats/goals   { } -> { people: { [id]: { calls,
   *     rdvTaken, rdvDone, sales, revenue } }, defaults }; calls and rdvTaken a
   *     week, the others a month; calls = the daily call goals × 5; a 510
   *     gets only themself
   *   saveStatsGoals   stats/goals/save { people: { [id]: { rdvTaken?,
   *     rdvDone?, sales?, revenue? } } } -> as stats/goals; 500 only; 400
   *     INVALID_GOAL (calls, or not a whole number from 0)
   *   getAgencyGoals   agency-goals  { year } -> { year, global, coefficient,
   *     sizes: { [size]: { start, growth, max } }, plan: { [territory]:
   *     { [size]: [12 counts, January first] } }, territories }; defaults
   *     without a saved plan (global 0, coefficient 1.65)
   *   saveAgencyGoals  agency-goals/save { year, global, coefficient, sizes,
   *     plan } -> as agency-goals; 400 INVALID_AGENCY_GOALS
   *   getAgencyGoalsProgress  agency-goals/progress { year } -> { [territory]:
   *     { [size]: { clients, revenue } } }: the sales of the year by territory
   *     and current size, and their revenue since
   * The three agency-goals routes and stats/goals/save are for the 500 only.
   */
  getStatsDays: toApi('getCrmStatsDays', ({ from, to, people }) => ({ from, to, people: people ?? [] })),
  getStatsReviews: toApi('getCrmStatsReviews', ({ people }) => ({ people: people ?? [] })),
  saveStatsReview: toApi('saveCrmStatsReview', (payload) => ({
    weekStart: payload.weekStart,
    ...Object.fromEntries(REVIEW_SCALES.map((scale) => [scale, payload[scale]])),
    victory: payload.victory ?? '',
    challenge: payload.challenge ?? '',
  })),
  suggestStatsChallenge: toApi('suggestCrmStatsChallenge', ({ weekStart, victory }) => ({
    weekStart,
    ...(victory ? { victory } : {}),
  })),
  getStatsGoals: toApi('getCrmStatsGoals', pick()),
  saveStatsGoals: toApi('saveCrmStatsGoals', ({ people }) => ({
    people: Object.fromEntries(
      Object.entries(people ?? {}).map(([id, goals]) => [
        id,
        Object.fromEntries(SAVED_GOALS.filter((key) => key in (goals ?? {})).map((key) => [key, goals[key]])),
      ]),
    ),
  })),
  getAgencyGoals: toApi('getCrmAgencyGoals', pick('year')),
  saveAgencyGoals: toApi('saveCrmAgencyGoals', pick('year', 'global', 'coefficient', 'sizes', 'plan')),
  getAgencyGoalsProgress: toApi('getCrmAgencyGoalsProgress', pick('year')),
};

export default allCrmApi;
